import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Building2 } from 'lucide-react';
import { partnersApiV2 } from '../services/api';
import { resolveBackendImage } from '../utils/brandLogo';

export default function PartnersSlider({ municipioSlug }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(4);

  useEffect(() => {
    loadPartners();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioSlug]);

  const loadPartners = async () => {
    try {
      const data = await partnersApiV2.list(municipioSlug);
      setPartners(data);
    } catch (error) {
      console.error('Erro ao carregar parceiros:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setItemsPerView(2);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(3);
      } else {
        setItemsPerView(4);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (partners.length <= itemsPerView) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const maxIndex = Math.max(0, partners.length - itemsPerView);
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [itemsPerView, partners.length]);

  const nextSlide = () => {
    const maxIndex = Math.max(0, partners.length - itemsPerView);
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    const maxIndex = Math.max(0, partners.length - itemsPerView);
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  // Don't render if no partners
  if (!loading && partners.length === 0) {
    return null;
  }

  return (
    <section className="py-12 bg-gray-50 border-t border-gray-200" data-testid="partners-slider">
      <div className="max-w-6xl mx-auto px-4">
        {/* Title */}
        <h2 className="text-xl md:text-2xl font-bold text-center mb-2 text-gray-800" style={{ fontFamily: 'Manrope' }}>
          Empresas Parceiras
        </h2>
        <p className="text-center text-gray-500 mb-8">
          Juntos pelo desenvolvimento local
        </p>

        {loading ? (
          <div className="flex justify-center gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="w-1/4 h-28 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : (
          /* Slider Container */
          <div className="relative">
            {/* Navigation Arrows */}
            {partners.length > itemsPerView && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-all"
                  aria-label="Parceiros anteriores"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>

                <button
                  onClick={nextSlide}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-all"
                  aria-label="Próximos parceiros"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </>
            )}

            {/* Partners List */}
            <div className="overflow-hidden mx-8">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{
                  transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
                }}
              >
                {partners.map((partner) => (
                  <div
                    key={partner.id}
                    className="flex-shrink-0 px-4"
                    style={{ width: `${100 / itemsPerView}%` }}
                  >
                    {partner.link ? (
                      <a 
                        href={partner.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        title={partner.descricao || partner.nome}
                        className="block bg-white rounded-xl p-6 h-28 flex items-center justify-center shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                      >
                        <img
                          src={resolveBackendImage(partner.logo_url)}
                          alt={partner.descricao || partner.nome}
                          className="max-h-14 max-w-full object-contain grayscale hover:grayscale-0 transition-all"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <span className="hidden text-gray-400 font-medium text-center text-sm">
                          {partner.nome}
                        </span>
                      </a>
                    ) : (
                      <div
                        title={partner.descricao || partner.nome}
                        className="bg-white rounded-xl p-6 h-28 flex items-center justify-center shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                      >
                        <img
                          src={resolveBackendImage(partner.logo_url)}
                          alt={partner.descricao || partner.nome}
                          className="max-h-14 max-w-full object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <span className="hidden text-gray-400 font-medium text-center text-sm">
                          {partner.nome}
                        </span>
                      </div>
                    )}
                    {partner.descricao && (
                      <p
                        className="mt-2 text-xs text-center text-gray-600 line-clamp-2 leading-snug px-1"
                        title={partner.descricao}
                      >
                        {partner.descricao}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <p className="text-center text-sm text-gray-500 mt-8">
          Quer ser parceiro? {' '}
          <a href="/contato" className="text-[var(--primary)] font-medium hover:underline">
            Entre em contato
          </a>
        </p>
      </div>
    </section>
  );
}
